/**
 * The server-rendered workout share card, as an Instagram story (1080×1920)
 * or a feed post (1080×1350): its URL, a download, or the OS share sheet.
 *
 * Used by ShareCardModal, which every share button opens (the public share
 * page, your own workout history, and a coach's view of a client's history).
 */

export const CARD_FORMATS = {
  story: { label: 'Story', size: '1080 × 1920', ratio: '9 / 16' },
  post: { label: 'Post', size: '1080 × 1350', ratio: '4 / 5' },
};

// The viewer's IANA zone, so the card's date and week read in local time (an
// evening workout is already tomorrow in UTC). Without it the server uses the
// client's own zone.
function localZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_) {
    return '';
  }
}

/** The card's image URL. The route is token-gated, nothing else. */
export function workoutCardUrl(workout, format = 'post') {
  const apiBase = process.env.REACT_APP_API_URL || '';
  const tz = localZone();
  return `${apiBase}/api/workout/share/${encodeURIComponent(workout.token)}/image.png?format=${format}${
    tz ? `&tz=${encodeURIComponent(tz)}` : ''
  }`;
}

async function fetchCard(workout, format) {
  if (!workout?.token) {
    throw new Error('share card: workout has no share token');
  }
  const res = await fetch(workoutCardUrl(workout, format));
  if (!res.ok) {
    throw new Error(`share image fetch failed (${res.status})`);
  }
  const blob = await res.blob();
  const safeName = (workout.name || 'workout').replace(/[^\w-]+/g, '_');
  const fileName = `${safeName}-${format}.png`;
  return { blob, fileName, file: new File([blob], fileName, { type: 'image/png' }) };
}

/** Save the card as a PNG. */
export async function downloadWorkoutImage(workout, format = 'post') {
  const { blob, fileName } = await fetchCard(workout, format);
  // A programmatic <a download> click from the already-fetched blob
  // downloads reliably — unlike async `window.open`, which the popup
  // blocker kills because the await broke the user-gesture chain.
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Whether this device can hand an image to the OS share sheet. Touch-primary
 * devices only: macOS Chrome reports `canShare({files}) === true` but its
 * sheet silently no-ops, so desktops get a download instead.
 */
export function canShareImages() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const touchPrimary =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  if (!touchPrimary || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({ files: [new File([''], 'card.png', { type: 'image/png' })] });
  } catch (_) {
    return false;
  }
}

/**
 * Hand the card to the OS share sheet (Instagram et al.). Returns
 * { shared: true }, or { shared: false } when the person dismissed the sheet.
 * Throws on fetch / share failure — callers handle their own toasts.
 */
export async function shareWorkoutImage(workout, format = 'post') {
  const { file } = await fetchCard(workout, format);
  try {
    await navigator.share({
      files: [file],
      title: workout.name || 'Workout',
    });
    return { shared: true };
  } catch (err) {
    if (err?.name === 'AbortError') return { shared: false };
    throw err;
  }
}
